import React, { useState, useEffect, useCallback } from "react"
import { Button, Table, Drawer, Form, Input, message, Popconfirm, Upload, Slider, Spin } from "antd"
import { PlusOutlined, EnvironmentOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from "@ant-design/icons"
import { useSelector, useDispatch } from "react-redux"
import { GoogleMap, Marker, Circle, LoadScript } from "@react-google-maps/api"
import { secureApi } from "../../Config/api"
import { GET, POST } from "../../utils/apis"
import { setGeofencesData, setGeofencePage, setGeofencePageSize, setTotalGeofences } from "../../Redux/actions/geofenceAction"
import { verifyToken } from "../../utils/helpers"
import { useHistory } from "react-router-dom"
import { translations } from "../../utils/constants"
import { ArrowDownToLine } from "lucide-react"
import axios from "axios"
import { DeleteModal } from "../../Components"

const GOOGLE_LIBRARIES = ['drawing', 'geometry']
const GOOGLE_MAPS_API_KEY = "AIzaSyAOvlaMKD4C0dPSjCtNOi-DnwkW8s4pSbI"

const mapContainerStyle = {
  width: '100%',
  height: '350px',
  minHeight: '350px',
  borderRadius: '12px',
  marginTop: '15px',
  border: '1px solid #ddd',
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: '#f0f0f0'
}

const GeoFencing = ({ language }) => {
  const t = translations[language]
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const history = useHistory()
  const user = useSelector((state) => state.authReducer?.user)
  const geofences = useSelector((state) => state.geofence?.geofences)
  const { page, pageSize, totalItems } = useSelector((state) => state.geofence)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [search, setSearch] = useState("")
  const [tableLoading, setTableLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [deviceLoading, setDeviceLoading] = useState(false)
  const [fileList, setFileList] = useState([])
  const [showMap, setShowMap] = useState(false)
  const [mapConfig, setMapConfig] = useState({ latitude: 0, longitude: 0, radius: 200 })
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null)
  const MAX_RADIUS = 5000

  const routerSerialNoValue = Form.useWatch('routerSerialNo', form);

  useEffect(() => {
    verifyToken(history, dispatch)
  }, [dispatch, history])

  const fetchGeofences = useCallback(async () => {
    try {
      setTableLoading(true)
      const res = await secureApi.get(`${GET?.GET_GEOFENCES}?search=${encodeURIComponent(search)}&id=${user?.id}&role=${user?.role}&page=${page}&limit=${pageSize}`)
      if (res?.success) {
        dispatch(setGeofencesData(res?.data))
        dispatch(setTotalGeofences(Number(res?.pagination?.total || 0)))
      }
    } catch (err) {
      message.error(t?.fetchError || "Data fetch failed")
    } finally {
      setTableLoading(false)
    }
  }, [search, user, page, pageSize, dispatch, t])

  useEffect(() => {
    fetchGeofences()
  }, [fetchGeofences])

  const handleDeviceSearch = async () => {
    try {
      const values = await form.validateFields(['routerSerialNo'])
      setDeviceLoading(true)
      const res = await secureApi.get(GET?.GET_DEVICE_LOCATION, { params: { serial_number: values?.routerSerialNo } })

      if (res?.success && res.data) {
        const lat = parseFloat(res.data.latitude)
        const lng = parseFloat(res.data.longitude)
        setMapConfig({ latitude: lat, longitude: lng, radius: 200 })
        setShowMap(true)
        message.success(t?.locationFound || "Location found!")
      } else {
        message.error(res?.message === 'No serial number found.' ? t?.noSerialNumberFound : (res?.message || t?.locationNotFound || "Location not found"))
        setShowMap(false)
      }
    } catch (err) {
      console.error(err)
    } finally {
      setDeviceLoading(false)
    }
  }

  const onFinish = async (values) => {
    if (fileList.length === 0) {
      return form.setFields([{ name: 'boatLogo', errors: [t?.boatLogoReq || 'Boat logo is required'] }])
    }
    if (!showMap) {
      return message.error(t?.fetchLocationFirst || "Please fetch device location first")
    }

    try {
      setSubmitLoading(true)
      let finalLogoUrl = fileList[0]?.url || ""

      if (fileList.length > 0 && fileList[0].originFileObj) {
        const formData = new FormData()
        formData.append("files", fileList[0].originFileObj)
        const uploadRes = await secureApi.post(POST.UPLOAD_VEHICLE_LOGO, formData)
        if (uploadRes?.success) finalLogoUrl = uploadRes?.fileUrls[0]
      }

      const payload = {
        boatName: values.boatName,
        serialNumber: values.routerSerialNo,
        radius: mapConfig.radius,
        centerLat: mapConfig.latitude,
        centerLng: mapConfig.longitude,
        createdBy: user?.id,
        logoUrl: finalLogoUrl
      }

      const res = editingId
        ? await secureApi.post(POST?.UPDATE_GEOFENCE, { ...payload, id: editingId })
        : await secureApi.post(POST?.ADD_GEOFENCE, payload)

      if (res?.success) {
        message.success(editingId ? (t?.operationSuccess || res?.message) : (t?.geofenceAdded || res?.message))
        handleCancel()
        fetchGeofences()
      }
      if (res?.success === false) {
        message.error(res?.error === 'Geofence already exists for this router' ? t?.geofenceAlreadyExists : res?.error)
      }
    } catch (err) {
      message.error(err)
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleDelete = async (id) => {
    try {
      const res = await secureApi.post(POST.DELETE_GEOFENCE, { id })
      if (res?.success) {
        message.success(t?.deleteSuccess || "Deleted successfully")
        fetchGeofences()
      }
      if (res?.success === false) {
        message.error(res?.message)
      }
    } catch (err) {
      message.error("Delete failed")
    }
  }

  const handleCancel = () => {
    setDrawerVisible(false)
    setEditingId(null)
    setFileList([])
    setShowMap(false)
    form.resetFields()
    setMapConfig({ latitude: 0, longitude: 0, radius: 200 })
  }

  const confirmDelete = async () => {
    if (recordToDelete) {
      await handleDelete(recordToDelete);
      setIsDeleteModalOpen(false);
      setRecordToDelete(null);
    }
  }

  const columns = [
    {
      title: "Logo",
      dataIndex: "logo_url",
      render: (src) => src ? <img src={src} width={40} height={40} style={{ borderRadius: 6, objectFit: 'cover' }} alt="logo" /> : "-"
    },
    { title: t?.boatName || "Boat Name", dataIndex: "boat_name" },
    { title: t?.serialNumber || "Serial No", dataIndex: "serial_number" },
    { title: `${t?.radius || "Radius"} (m)`, dataIndex: "radius", render: (r) => `${r}m` },
    { title: t?.latitude, dataIndex: "latitude" },
    { title: t?.longitude, dataIndex: "longitude" },
    {
      title: t?.actions || "Actions",
      render: (_, record) => (
        <div style={{ display: 'flex', gap: "10px" }}>
          <Button
            type="link"
            icon={<EditOutlined />}

            onClick={() => {
              setEditingId(record.id)
              form.setFieldsValue({
                boatName: record.boat_name,
                routerSerialNo: record.serial_number
              })
              setMapConfig({
                latitude: parseFloat(record.latitude),
                longitude: parseFloat(record.longitude),
                radius: parseInt(record.radius)
              })
              if (record.logo_url) {
                setFileList([
                  {
                    uid: '-1',
                    name: 'boat-logo.png',
                    status: 'done',
                    url: record.logo_url,
                  },
                ])
              } else {
                setFileList([])
              }
              setShowMap(true)
              setDrawerVisible(true)
            }}
          />
          <Button type="link" onClick={() => { setRecordToDelete(record.id); setIsDeleteModalOpen(true) }} danger icon={<DeleteOutlined />} />
        </div>
      )
    }
  ]

  const handleDownloadPDF = async () => {
    try {
      setDownloadLoading(true);
      const params = {
        user_id: user?.id,
        search: search,
        language: language
      };

      const res = await axios.get(GET?.GET_DOWNLOAD_GEOFENCE_PDF, { params })
      if (res?.data?.success && res?.data?.data) {
        const pdfUrl = res.data.data;

        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `${language === 'it' ? 'Rapporto_Geofencing' : 'Geofencing_Report'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        message.success(
          language === 'it'
            ? 'Rapporto geofencing scaricato con successo'
            : 'Geofencing report downloaded successfully'
        );
      } else {
        message.error(
          res?.data?.message ||
          (language === 'it'
            ? 'Impossibile scaricare il rapporto geofencing'
            : 'Failed to download geofencing report')
        );
      }
    } catch (error) {
      console.error('Geofence PDF download error:', error);
      message.error(
        language === 'it'
          ? 'Errore durante il download del rapporto geofencing'
          : 'Error downloading geofencing report'
      );
    } finally {
      setDownloadLoading(false);
    }
  }

  return (
    <div style={{ padding: 24 }}>
      <div className="home-header" style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", alignItems: "center" }}>
        <div>
          <h1 className="timesheet-title">{t?.geofencing || "GeoFencing"}</h1>
          <p className="timesheet-subtitle">{t?.geofencingSubtitle || "Manage boat perimeters and boundaries"}</p>
        </div>
        <div style={{ display: "flex", gap: "1rem" }}>
          <Input
            placeholder={t?.searchBoat || "Search Boat..."}
            prefix={<SearchOutlined />}
            className="antd-search-input"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              dispatch(setGeofencePage(1))
            }}
            style={{ width: 200 }}
            allowClear
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="export-btn"
            onClick={() => {
              setEditingId(null)
              form.resetFields()
              setDrawerVisible(true)
            }}
          >
            {t?.addGeofencing || "Add Geofencing"}
          </Button>
        </div>
      </div>

      <div className='table-container'>
        <div className='table-header' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h2 className='h1-theme-color margin-bottom-0'>{translations?.[language]?.geofenceList}</h2>
          </div>
          <div>
            <Button
              type="primary"
              loading={downloadLoading}
              className="export-btn"
              style={{ display: 'inline-flex', alignItems: 'center' }}
              onClick={handleDownloadPDF}
            >
              <ArrowDownToLine style={{ height: '15px' }} className='icons' />
              {translations?.[language]?.downloadPDF}
            </Button>
          </div>
        </div>
        <Table
          dataSource={geofences}
          columns={columns}
          loading={tableLoading}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          className="custom-timesheet-table"
          pagination={{
            current: page,
            pageSize,
            total: totalItems,
            showSizeChanger: true,
            onChange: (nextPage, nextPageSize) => {
              dispatch(setGeofencePage(nextPageSize !== pageSize ? 1 : nextPage))
              dispatch(setGeofencePageSize(nextPageSize))
            }
          }}
        />
      </div>

      <Drawer
        title={editingId ? (t?.editGeofencing || "Edit Geofence") : (t?.addGeofencing )}
        width={720}
        open={drawerVisible}
        onClose={handleCancel}
        destroyOnClose
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
            <Button onClick={handleCancel} className="cancel-button">
              {t?.cancel || "Cancel"}
            </Button>
            <Button
              type="primary"
              loading={submitLoading}
              onClick={() => form.submit()}
              className="submit-button"
            >
              {editingId ? (t?.editGeofencing || "Update GeoFence") : (t?.addGeofencing )}
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="boatName"
            label={t?.boatName || "Boat Name"}
            rules={[{ required: true, message: t?.boatNameReq || 'Boat name is required' }]}
          >
            <Input className="antd-input" placeholder={t?.enterBoatName || "Enter boat name"} />
          </Form.Item>

          <Form.Item label={t?.routerSerialNo || "Router Serial Number"} required style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <Form.Item
                name="routerSerialNo"
                rules={[{ required: true, message: t?.serialNoReq || 'Serial number is required' }]}
                style={{ flex: 1, marginBottom: '24px' }}
              >
                <Input
                  className="antd-input drawer-input"
                  placeholder={t?.enterSerialNo || "Enter Serial number"}
                  style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, height: '40px' }}
                />
              </Form.Item>
              <Button
                type="primary"
                icon={<EnvironmentOutlined />}
                onClick={handleDeviceSearch}
                loading={deviceLoading}
                disabled={!routerSerialNoValue}
                style={{
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  height: '40px',
                  backgroundColor: !routerSerialNoValue ? '#f5f5f5' : '#1890ff',
                  borderColor: !routerSerialNoValue ? '#d9d9d9' : '#1890ff'
                }}
              />
            </div>
          </Form.Item>

          {showMap && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: '12px' }}>
                <span>{t?.dragMarker || "Drag marker to adjust location"}</span>
                <b>{t?.radius || "Radius"}: <span style={{ color: '#1890ff' }}>{mapConfig.radius}m</span></b>
              </div>
              <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={GOOGLE_LIBRARIES}              >
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={{ lat: mapConfig.latitude, lng: mapConfig.longitude }}
                  zoom={15}
                  options={{
                    gestureHandling: 'greedy',
                    mapTypeControl: false,
                    streetViewControl: false
                  }}
                >
                  <Marker
                    position={{ lat: mapConfig.latitude, lng: mapConfig.longitude }}
                    draggable={true}
                    onDragEnd={(e) => setMapConfig(prev => ({ ...prev, latitude: e.latLng.lat(), longitude: e.latLng.lng() }))}
                  />
                  <Circle
                    center={{ lat: mapConfig.latitude, lng: mapConfig.longitude }}
                    radius={mapConfig.radius}
                    options={{
                      fillColor: '#1890ff',
                      fillOpacity: 0.2,
                      strokeColor: '#1890ff',
                      strokeWeight: 1,
                    }}
                  />
                </GoogleMap>
              </LoadScript>

              <div style={{ marginTop: 20 }}>
                <label style={{ fontSize: '13px', fontWeight: 500 }}>{t?.adjustRadius || "Adjust Perimeter Radius"}:</label>
                <Slider
                  min={50}
                  max={MAX_RADIUS}
                  step={50}
                  value={mapConfig.radius}
                  onChange={(val) => setMapConfig(prev => ({ ...prev, radius: val }))}
                />
              </div>
            </div>
          )}

          <Form.Item
            label={t?.boatLogo || "Boat Logo"}
            name="boatLogo"
            required
            rules={[
              {
                validator: (_, value) => {
                  if (fileList.length > 0) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t?.boatLogoReq || "Boat logo is required"));
                },
              },
            ]}
          >
            <Upload
              listType="picture-card"
              fileList={fileList}
              onChange={({ fileList: nextFileList }) => {
                setFileList(nextFileList)
                form.setFieldsValue({ boatLogo: nextFileList })
                form.validateFields(['boatLogo']).catch(() => {})
              }}
              accept="image/*"
              beforeUpload={(file) => {
                const isImage = file.type?.startsWith('image/')
                if (!isImage) {
                  message.error(t?.onlyImageAllowed || 'Only image files are allowed')
                  return Upload.LIST_IGNORE
                }
                return false
              }}
              maxCount={1}
            >
              {fileList.length < 1 && (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>{t?.upload || "Upload"}</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </Form>
      </Drawer>
      <DeleteModal
        title={translations[language]?.deleteConfirm || "Confirm Delete"}
        bodyText={translations[language]?.deleteGeofenceMessage}
        isOpen={isDeleteModalOpen}
        language={language}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setRecordToDelete(null);
        }}
        onDelete={confirmDelete}
      />
    </div>
  )
}

export default GeoFencing
